import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Download, CheckCircle2, AlertCircle, TrendingUp, Target, Award } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StudentReport() {
  const { submission_id } = useParams();
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/marking/submissions/${submission_id}/report`, { withCredentials: true });
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error(error.response?.data?.detail || 'Failed to load report');
      navigate('/assessment');
    } finally {
      setLoading(false);
    }
  }, [submission_id, navigate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const downloadPDF = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${API}/marking/submissions/${submission_id}/export/pdf`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.student_name}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const downloadParentReport = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(
        `${API}/marking/submissions/${submission_id}/parent-report/pdf`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.student_name}_parent_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Parent report downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download parent report');
    } finally {
      setDownloading(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A': 'bg-green-100 text-green-700 border-green-300',
      'B': 'bg-blue-100 text-blue-700 border-blue-300',
      'C': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'D': 'bg-orange-100 text-orange-700 border-orange-300',
      'F': 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[grade] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Generating report...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <button
                onClick={() => navigate('/assessment')}
                className="text-purple-600 hover:text-purple-700 font-medium mb-4 inline-flex items-center gap-2"
              >
                ← Back to Assessment Hub
              </button>
              <h1 className="text-3xl font-bold text-slate-900">Student Performance Report</h1>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={downloadPDF}
                disabled={downloading}
                className="bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95 inline-flex items-center gap-2 disabled:bg-slate-300"
              >
                <Download className="w-5 h-5" />
                {downloading ? 'Downloading...' : 'Teacher Report (PDF)'}
              </button>
              
              <button
                onClick={downloadParentReport}
                disabled={downloading}
                className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-xl font-semibold shadow-sm transition-all active:scale-95 inline-flex items-center gap-2 disabled:bg-slate-300"
              >
                <Download className="w-5 h-5" />
                {downloading ? 'Downloading...' : 'Parent Report (PDF)'}
              </button>
            </div>
          </div>

          {/* Student Info Card */}
          <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm mb-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-slate-600 mb-1">Student Name</p>
                <p className="text-xl font-bold text-slate-900">{report.student_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Assessment</p>
                <p className="text-xl font-bold text-slate-900">{report.project_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Subject</p>
                <p className="text-base font-semibold text-slate-900">{report.subject || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 mb-1">Grade Level & Curriculum</p>
                <p className="text-base font-semibold text-slate-900">Grade {report.grade_level} • {report.curriculum}</p>
              </div>
            </div>
          </div>

          {/* Score Summary */}
          <div className="bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl p-8 text-white shadow-lg mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-purple-100 mb-2">Overall Score</p>
                <p className="text-5xl font-bold">{report.total_marks?.toFixed(1)} / {report.max_marks?.toFixed(1)}</p>
              </div>
              <div className="text-center">
                <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-2xl border-2 font-bold text-2xl ${getGradeColor(report.grade)} bg-white`}>
                  <Award className="w-8 h-8" />
                  Grade {report.grade}
                </div>
                <p className="text-3xl font-bold mt-2">{report.percentage?.toFixed(1)}%</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-purple-400">
              <div className="text-center">
                <p className="text-purple-100 text-sm mb-1">Excellent</p>
                <p className="text-2xl font-bold">{report.excellent_answers}</p>
              </div>
              <div className="text-center">
                <p className="text-purple-100 text-sm mb-1">Good</p>
                <p className="text-2xl font-bold">{report.good_answers}</p>
              </div>
              <div className="text-center">
                <p className="text-purple-100 text-sm mb-1">Needs Work</p>
                <p className="text-2xl font-bold">{report.needs_improvement_answers}</p>
              </div>
            </div>
          </div>

          {/* Strengths */}
          <div className="bg-white rounded-2xl border border-green-200 p-6 shadow-sm mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Strengths</h2>
            </div>
            
            {report.strengths.length > 0 ? (
              <ul className="space-y-2">
                {report.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-700">
                    <span className="text-green-600 font-bold mt-1">✓</span>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-600">No specific strengths identified yet.</p>
            )}
          </div>

          {/* Areas for Improvement */}
          <div className="bg-white rounded-2xl border border-amber-200 p-6 shadow-sm mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Areas for Improvement</h2>
            </div>
            
            {report.areas_for_improvement.length > 0 ? (
              <ul className="space-y-2">
                {report.areas_for_improvement.map((area, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-700">
                    <span className="text-amber-600 font-bold mt-1">•</span>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-600">Excellent performance across all areas!</p>
            )}
          </div>

          {/* Question-by-Question Breakdown */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Detailed Breakdown</h2>
            </div>

            <div className="space-y-4">
              {report.detailed_answers.sort((a, b) => a.question_number - b.question_number).map((answer) => (
                <div key={answer.question_number} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-slate-900">Question {answer.question_number}</h3>
                    <span className="text-lg font-bold text-slate-900">
                      {answer.suggested_mark?.toFixed(1)} / {answer.max_mark?.toFixed(1)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">
                    <strong>Student Answer:</strong> {answer.extracted_text}
                  </p>
                  <p className="text-sm text-slate-700">
                    <strong>Feedback:</strong> {answer.feedback}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center text-sm text-slate-500">
            <p>Report generated on {new Date(report.generated_at).toLocaleDateString()}</p>
            <p className="mt-1">Learn with Zeenat • AI-Assisted Assessment</p>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default StudentReport;
